import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Target, Plus, Search, Filter, TrendingUp, DollarSign, CheckCircle,
  BarChart3, MoreVertical, Trash2, Edit, Eye, ArrowLeft, Megaphone
} from 'lucide-react';
import { apiV1 } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { FEATURES } from '@/lib/features';
import { FeatureUnavailable } from '@/components/data-display/FeatureUnavailable';

// Keys are the backend GoalType values.
const goalTypeConfig = {
  form_submission: { label: 'Form Submission', icon: CheckCircle, color: 'bg-blue-500' },
  page_view: { label: 'Page View', icon: Eye, color: 'bg-purple-500' },
  qr_scan: { label: 'QR Scan', icon: Target, color: 'bg-emerald-500' },
  link_click: { label: 'Link Click', icon: TrendingUp, color: 'bg-amber-500' },
  custom_event: { label: 'Custom', icon: BarChart3, color: 'bg-slate-500' },
};

/**
 * Conversion goals are switched off by default (lib/features.js): nothing
 * records conversions against a goal yet, so every goal would read 0.
 * REACT_APP_FEATURE_CONVERSION_GOALS=true shows the screen.
 */
export default function Conversions() {
  if (!FEATURES.conversionGoals) {
    return (
      <FeatureUnavailable
        title="Conversion Tracking"
        reason="Conversion goals are not connected to any tracking yet. Form submissions and leads are on the Submissions and Leads pages."
      />
    );
  }
  return <ConversionsScreen />;
}

export function ConversionsScreen() {
  // /conversions?campaign_id=<id>  or  /conversions?goal_id=<id>
  // scopes the list to a single campaign or goal. The backend
  // /api/v1/conversions/ endpoint already accepts these filters; this
  // just wires them through. Calls swapped to apiV1 so auth headers
  // are attached (raw fetch was bypassing the auth interceptor).
  const [searchParams] = useSearchParams();
  const campaignIdFilter = searchParams.get('campaign_id') || null;
  const goalIdFilter = searchParams.get('goal_id') || null;
  const scopedToSomething = campaignIdFilter || goalIdFilter;

  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const [newGoal, setNewGoal] = useState({
    name: '',
    description: '',
    type: 'form_submission',
    monetary_value: 0,
  });

  useEffect(() => {
    loadGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignIdFilter, goalIdFilter]);

  const loadGoals = async () => {
    setLoading(true);
    try {
      const params = {};
      if (campaignIdFilter) params.campaign_id = campaignIdFilter;
      // /conversions/goals has no goal_id filter; a single goal is /goals/{id}.
      const response = goalIdFilter
        ? await apiV1.get(`/conversions/goals/${goalIdFilter}`)
        : await apiV1.get('/conversions/goals', { params });
      if (response.data?.success) {
        const d = response.data.data;
        setGoals(Array.isArray(d) ? d : (d ? [d] : []));
      }
    } catch (error) {
      toast.error('Failed to load conversion goals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newGoal.name.trim()) {
      toast.error('Goal name is required');
      return;
    }

    try {
      const payload = { ...newGoal };
      if (campaignIdFilter) payload.campaign_id = campaignIdFilter;
      const response = await apiV1.post('/conversions/goals', payload);
      if (response.data?.success) {
        toast.success('Conversion goal created!');
        setShowCreateDialog(false);
        setNewGoal({ name: '', description: '', type: 'form_submission', monetary_value: 0 });
        loadGoals();
      } else {
        toast.error(response.data?.detail || 'Failed to create goal');
      }
    } catch (error) {
      toast.error('Failed to create conversion goal');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      const response = await apiV1.delete(`/conversions/goals/${id}`);
      if (response.data?.success) {
        toast.success('Goal deleted');
        loadGoals();
      }
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const filteredGoals = goals.filter(g =>
    g.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalConversions = goals.reduce((sum, g) => sum + (g.total_conversions || 0), 0);
  const totalValue = goals.reduce((sum, g) => sum + ((g.total_conversions || 0) * (g.monetary_value || 0)), 0);

  return (
    <div className="space-y-6" data-testid="conversions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {scopedToSomething ? 'Conversion Tracking (filtered)' : 'Conversion Tracking'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {scopedToSomething
              ? 'Scoped to a single campaign or goal'
              : 'Define and track your conversion goals'}
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-goal-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Goal
        </Button>
      </div>

      {scopedToSomething && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Megaphone className="h-5 w-5 text-primary" />
              <div className="text-sm">
                <p className="font-medium">
                  {campaignIdFilter
                    ? <>Filtered to campaign: <code className="bg-muted px-1 rounded">{campaignIdFilter}</code></>
                    : <>Filtered to goal: <code className="bg-muted px-1 rounded">{goalIdFilter}</code></>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {campaignIdFilter && (
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/campaigns/${campaignIdFilter}`}>Open campaign</Link>
                </Button>
              )}
              <Button variant="outline" size="sm" asChild>
                <Link to="/conversions">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  All conversions
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Goals</p>
                <p className="text-2xl font-bold">{goals.filter(g => g.status === 'active').length}</p>
              </div>
              <Target className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Conversions</p>
                <p className="text-2xl font-bold text-emerald-600">{totalConversions}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversion Value</p>
                <p className="text-2xl font-bold text-blue-600">${totalValue.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Rate</p>
                <p className="text-2xl font-bold text-purple-600">
                  {goals.length > 0 ? (goals.reduce((sum, g) => sum + (g.conversion_rate || 0), 0) / goals.length).toFixed(1) : 0}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search goals..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-goals"
        />
      </div>

      {/* Goals Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-muted rounded w-3/4 mb-4" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredGoals.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No conversion goals yet</h3>
            <p className="text-muted-foreground mb-4">Create your first goal to start tracking</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Goal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="goals-grid">
          {filteredGoals.map(goal => {
            const config = goalTypeConfig[goal.type] || goalTypeConfig.custom_event;
            const Icon = config.icon;
            return (
              <Card key={goal.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center text-white`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{goal.name}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {config.label}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDelete(goal.id)} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  {goal.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {goal.description}
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-muted/50 rounded">
                      <p className="text-lg font-semibold">{goal.total_conversions || 0}</p>
                      <p className="text-xs text-muted-foreground">Conversions</p>
                    </div>
                    <div className="p-2 bg-muted/50 rounded">
                      <p className="text-lg font-semibold">{goal.conversion_rate || 0}%</p>
                      <p className="text-xs text-muted-foreground">Rate</p>
                    </div>
                    <div className="p-2 bg-muted/50 rounded">
                      <p className="text-lg font-semibold">R{goal.monetary_value || 0}</p>
                      <p className="text-xs text-muted-foreground">Value</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Conversion Goal</DialogTitle>
            <DialogDescription>Define what counts as a successful conversion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Goal Name *</Label>
              <Input
                id="name"
                placeholder="Contact Form Submission"
                value={newGoal.name}
                onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                data-testid="goal-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe this conversion goal..."
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Goal Type</Label>
              <Select
                value={newGoal.type}
                onValueChange={(value) => setNewGoal({ ...newGoal, type: value })}
              >
                <SelectTrigger data-testid="goal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(goalTypeConfig).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="value">Conversion Value (R)</Label>
              <Input
                id="value"
                type="number"
                placeholder="100"
                value={newGoal.monetary_value}
                onChange={(e) => setNewGoal({ ...newGoal, monetary_value: parseFloat(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">Estimated value of each conversion</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-goal-submit">
              Create Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
